/**
 * usePartKnowledge.js — client + hooks for the part-keyed knowledge API
 * (knowledge_packs/CONTRACT.md §3–4).
 *
 *   const parts = useParts('jet', { enabled, tree });
 *   parts.dossier(cadId, { includeChildren, signal })
 *   parts.trace(faultId, { signal })
 *   parts.ask({ question, focus_part_id, history }, { signal })
 *   parts.resolve(text, { signal })
 *   parts.coverage({ signal }) / parts.bom({ signal })
 *
 * Every call resolves (never rejects) to
 *   { ok: true, data }  |  { ok: false, reason: 'unavailable'|'timeout'|'not_found'|'aborted' }
 * so callers render calm terminal states and the 3D view never depends on the
 * service. Same spirit as the { success, error } shape in utils/knowledgeGraphAPI.js.
 *
 * Timeouts: ask 45 s (a cold backend's first local-model answer takes ~35 s),
 * everything else 9 s. 404 and 503 both render as 'unavailable' (asset not
 * part-enabled / graph not built), except /trace where 404 means no trace for
 * that message.
 * Caching: per client (= per asset) and per id; failures are never cached, so a
 * retry really retries. /ask is never cached.
 *
 * Fixture mode: VITE_PART_KNOWLEDGE_FIXTURES=1 serves SYNTHETIC JSON from
 * ./fixtures through the same normalisers (see fixtureServer.js).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const FIXTURE_MODE = import.meta.env.VITE_PART_KNOWLEDGE_FIXTURES === '1';
const API_BASE = String(import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000').replace(/\/+$/, '');

export const REQUEST_TIMEOUT_MS = 9000;
export const ASK_TIMEOUT_MS = 45000;
const FIXTURE_LATENCY_MS = 320;
const TAG = '[PartKnowledge]';

/* ------------------------------------------------------------ normalisers */

const arr = (v) => (Array.isArray(v) ? v : []);
const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : null);
const str = (v) => (typeof v === 'string' ? v : (v === null || v === undefined ? null : String(v)));
const strs = (v) => arr(v).filter((s) => typeof s === 'string' && s.trim());
const num = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);

function normaliseTask(t, ownerId) {
  return {
    id: str(t.id) ?? '',
    cad_id: str(t.cad_id) ?? ownerId,
    title: str(t.title) ?? 'Untitled task',
    type: str(t.type) ?? 'inspection',
    interval: str(t.interval),
    safety: strs(t.safety),
    tools: strs(t.tools),
    steps: strs(t.steps),
    access_parts: strs(t.access_parts),
    // Optional (vacuum_pump pack): service kit id such as 'SK-4000'; null when absent.
    service_kit: str(t.service_kit),
  };
}

function normaliseFailureMode(f, ownerId) {
  return {
    id: str(f.id) ?? '',
    cad_id: str(f.cad_id) ?? ownerId,
    mode: str(f.mode) ?? 'Unnamed failure mode',
    cause: str(f.cause),
    effect: str(f.effect),
    detection: str(f.detection),
    severity: str(f.severity) ?? 'minor',
    likelihood: num(f.likelihood, null),
    // Optional ISO 14224-style failure-mode code (vacuum_pump pack); null when absent.
    iso14224: str(f.iso14224),
  };
}

export function normaliseDossier(d) {
  if (!obj(d) || typeof d.cad_id !== 'string') return null;
  const id = d.cad_id;
  const ref = (x) => obj(x) && typeof x.cad_id === 'string';
  return {
    asset: str(d.asset),
    cad_id: id,
    name: str(d.name) ?? id,
    level: str(d.level),
    ata: str(d.ata),
    parent: str(d.parent),
    ancestors: arr(d.ancestors).filter(ref),
    children: arr(d.children).filter(ref),
    summary: str(d.summary),
    rollup_summary: str(d.rollup_summary),
    specs: arr(d.specs).filter(obj).map((s) => ({
      name: str(s.name) ?? '—', value: s.value ?? null, unit: str(s.unit) ?? '', note: str(s.note),
    })),
    failure_modes: arr(d.failure_modes).filter(obj).map((f) => normaliseFailureMode(f, id)),
    tasks: arr(d.tasks).filter(obj).map((t) => normaliseTask(t, id)),
    connections: arr(d.connections).filter((c) => obj(c) && ref(c.other)).map((c) => ({
      id: str(c.id) ?? '', type: str(c.type) ?? 'mechanical', label: str(c.label) ?? '',
      direction: c.direction === 'out' ? 'out' : 'in', other: c.other,
    })),
    fault_messages: arr(d.fault_messages).filter((m) => obj(m) && m.id),
    rollup: obj(d.rollup),
    coverage: obj(d.coverage),
    provenance: str(d.provenance) ?? '',
  };
}

export function normaliseTrace(d) {
  if (!obj(d) || !obj(d.fault)) return null;
  return {
    fault: d.fault,
    hypotheses: arr(d.hypotheses)
      .filter((h) => obj(h) && typeof h.cad_id === 'string')
      .map((h) => ({
        rank: num(h.rank, 99), cad_id: h.cad_id, name: str(h.name) ?? h.cad_id,
        role: str(h.role) ?? 'check', rationale: str(h.rationale),
        failure_mode: obj(h.failure_mode), check_task: obj(h.check_task),
      }))
      .sort((a, b) => a.rank - b.rank),
    paths: arr(d.paths).filter((p) => obj(p) && typeof p.from === 'string' && typeof p.to === 'string'),
    part_ids: strs(d.part_ids),
    provenance: str(d.provenance) ?? '',
  };
}

export function normaliseAsk(d) {
  if (!obj(d) || typeof d.answer !== 'string') return null;
  return {
    answer: d.answer,
    part_ids: arr(d.part_ids).filter((p) => obj(p) && typeof p.cad_id === 'string'),
    hops: arr(d.hops).filter((h) => obj(h) && typeof h.cad_id === 'string')
      .sort((a, b) => num(a.step) - num(b.step)),
    citations: arr(d.citations).filter(obj),
    used_llm: d.used_llm === true,
    provenance: str(d.provenance) ?? '',
  };
}

export function normaliseResolve(d) {
  if (!obj(d)) return null;
  return {
    matches: arr(d.matches).filter((m) => obj(m) && typeof m.cad_id === 'string')
      .sort((a, b) => num(b.score) - num(a.score)),
    provenance: str(d.provenance) ?? '',
  };
}

function normaliseNodeList(d) {
  if (!obj(d)) return null;
  return { ...d, nodes: arr(d.nodes).filter((n) => obj(n) && typeof n.id === 'string'), provenance: str(d.provenance) ?? '' };
}

/* ------------------------------------------------------------ transport */

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    }, { once: true });
  });
}

/** Resolve to an 'aborted' result as soon as `signal` fires, without cancelling `promise`. */
function raceAbort(promise, signal) {
  if (!signal) return promise;
  if (signal.aborted) return Promise.resolve({ ok: false, reason: 'aborted' });
  return new Promise((resolve) => {
    const onAbort = () => resolve({ ok: false, reason: 'aborted' });
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then((r) => { signal.removeEventListener('abort', onAbort); resolve(r); });
  });
}

export function createPartKnowledgeClient(asset, { getTree = () => null } = {}) {
  const inflight = new Map();
  const settled = new Map();
  const enc = encodeURIComponent;
  const base = `${API_BASE}/assembly/${enc(asset)}`;

  async function run(kind, { path, method = 'GET', body, timeoutMs, signal, normalise, fixtureArgs }) {
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => { timedOut = true; controller.abort(); }, timeoutMs);
    const onAbort = () => controller.abort();
    signal?.addEventListener('abort', onAbort, { once: true });
    try {
      let status;
      let data = null;
      if (FIXTURE_MODE) {
        await sleep(FIXTURE_LATENCY_MS, controller.signal);
        const { fixtureResponse } = await import('./fixtureServer.js');
        ({ status, data } = await fixtureResponse(kind, fixtureArgs, { asset, tree: getTree() }));
      } else {
        const res = await fetch(`${base}${path}`, {
          method,
          headers: body ? { 'Content-Type': 'application/json' } : undefined,
          body: body ? JSON.stringify(body) : undefined,
          signal: controller.signal,
        });
        status = res.status;
        if (res.ok) data = await res.json();
      }
      if (status === 404) {
        console.info(TAG, `${kind} HTTP 404`);
        return { ok: false, reason: kind === 'trace' ? 'not_found' : 'unavailable' };
      }
      if (status < 200 || status >= 300) {
        console.info(TAG, `${kind} HTTP ${status}`);
        return { ok: false, reason: 'unavailable' };
      }
      const norm = normalise(data);
      if (!norm) {
        console.warn(TAG, `${kind} response did not match the contract`);
        return { ok: false, reason: 'unavailable' };
      }
      return { ok: true, data: norm };
    } catch (err) {
      if (timedOut) return { ok: false, reason: 'timeout' };
      if (err?.name === 'AbortError') return { ok: false, reason: 'aborted' };
      console.info(TAG, `${kind} unavailable:`, err?.message || err);
      return { ok: false, reason: 'unavailable' };
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    }
  }

  /** Cached GET-like call: one shared request per key; callers abort only their own wait. */
  function cached(key, kind, opts) {
    const { signal, ...rest } = opts;
    if (settled.has(key)) return Promise.resolve(settled.get(key));
    let p = inflight.get(key);
    if (!p) {
      p = run(kind, { ...rest, timeoutMs: rest.timeoutMs ?? REQUEST_TIMEOUT_MS }).then((r) => {
        inflight.delete(key);
        if (r.ok) settled.set(key, r);
        return r;
      });
      inflight.set(key, p);
    }
    return raceAbort(p, signal);
  }

  return {
    asset,
    fixtureMode: FIXTURE_MODE,
    peek: (key) => settled.get(key)?.data ?? null,
    dossierKey: (cadId, includeChildren) => `dossier|${cadId}|${includeChildren ? 1 : 0}`,

    dossier(cadId, { includeChildren = false, signal } = {}) {
      return cached(`dossier|${cadId}|${includeChildren ? 1 : 0}`, 'dossier', {
        path: `/part/${enc(cadId)}?include_children=${includeChildren ? 'true' : 'false'}`,
        normalise: normaliseDossier,
        fixtureArgs: { cadId, includeChildren },
        signal,
      });
    },
    trace(faultId, { signal } = {}) {
      return cached(`trace|${faultId}`, 'trace', {
        path: `/trace/${enc(faultId)}`, normalise: normaliseTrace, fixtureArgs: { faultId }, signal,
      });
    },
    resolve(text, { signal } = {}) {
      const body = { text: String(text ?? '') };
      return cached(`resolve|${body.text.trim().toLowerCase()}`, 'resolve', {
        path: '/resolve', method: 'POST', body, normalise: normaliseResolve, fixtureArgs: body, signal,
      });
    },
    coverage({ signal } = {}) {
      return cached('coverage', 'coverage', { path: '/coverage', normalise: normaliseNodeList, fixtureArgs: {}, signal });
    },
    bom({ signal } = {}) {
      return cached('bom', 'bom', { path: '/bom', normalise: normaliseNodeList, fixtureArgs: {}, signal });
    },
    ask({ question, focus_part_id = null, history = [] }, { signal } = {}) {
      const body = { question: String(question ?? ''), focus_part_id, history };
      return run('ask', {
        path: '/ask', method: 'POST', body, timeoutMs: ASK_TIMEOUT_MS,
        normalise: normaliseAsk, fixtureArgs: body, signal,
      });
    },
  };
}

/* ------------------------------------------------------------ hooks */

/**
 * One client per asset. Returns null when disabled (legacy / none assets), so
 * callers can gate every knowledge feature on truthiness.
 */
export function useParts(asset, { enabled = true, tree = null } = {}) {
  const treeRef = useRef(tree);
  useEffect(() => { treeRef.current = tree; }, [tree]);
  return useMemo(
    () => (enabled && asset ? createPartKnowledgeClient(asset, { getTree: () => treeRef.current }) : null),
    [asset, enabled],
  );
}

/**
 * Generic request state for a keyed load. `key === null` means idle.
 * phase: 'idle' | 'loading' | 'ready' | 'error'  (reason set on error)
 * `keepWhile(prevKey, nextKey)` lets the previous data stay on screen while a
 * related key loads (e.g. toggling include-children on the same part).
 */
export function useKnowledgeQuery(load, key, { keepWhile = null, initial = null } = {}) {
  const [state, setState] = useState({ key: null, phase: 'idle', data: null, reason: null });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!key || typeof load !== 'function') {
      setState({ key: null, phase: 'idle', data: null, reason: null });
      return undefined;
    }
    const cachedData = initial ? initial(key) : null;
    if (cachedData) {
      setState({ key, phase: 'ready', data: cachedData, reason: null });
      return undefined;
    }
    const controller = new AbortController();
    setState((s) => ({
      key,
      phase: 'loading',
      data: keepWhile && s.key && keepWhile(s.key, key) ? s.data : null,
      reason: null,
    }));
    load(controller.signal).then((res) => {
      if (controller.signal.aborted || res.reason === 'aborted') return;
      setState(res.ok
        ? { key, phase: 'ready', data: res.data, reason: null }
        : { key, phase: 'error', data: null, reason: res.reason });
    });
    return () => controller.abort();
    // `load` is expected to be memoised on the same inputs as `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, nonce]);

  const retry = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, retry };
}

const samePart = (a, b) => a.split('|')[0] === b.split('|')[0];

export function useDossier(client, cadId, includeChildren = false) {
  const load = useCallback(
    (signal) => client.dossier(cadId, { includeChildren, signal }),
    [client, cadId, includeChildren],
  );
  const key = client && cadId ? `${cadId}|${includeChildren ? 1 : 0}|${client.asset}` : null;
  const initial = useCallback(
    () => (client && cadId ? client.peek(client.dossierKey(cadId, includeChildren)) : null),
    [client, cadId, includeChildren],
  );
  return useKnowledgeQuery(load, key, { keepWhile: samePart, initial });
}
